import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtIDR, fmtTanggal, fmtTanggalSingkat } from "../lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  Calculator,
  ArrowUpToLine,
  PenLine,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

const BULAN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

const tooltipFmt = (v) => fmtIDR(v);

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
    Promise.all([api.get("/saldo/summary"), api.get("/saldo")])
      .then(([s, e]) => {
        setSummary(s.data);
        setEntries(e.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const lineData = useMemo(
    () => entries.map((e) => ({ label: fmtTanggalSingkat(e.tanggal), saldo: e.total_saldo })),
    [entries]
  );

  const barData = useMemo(() => {
    const grouped = entries.reduce((acc, e) => {
      const m = e.tanggal.slice(0, 7);
      acc[m] = acc[m] || { total: 0, n: 0 };
      acc[m].total += e.total_saldo;
      acc[m].n += 1;
      return acc;
    }, {});
    return Object.entries(grouped).map(([m, v]) => {
      const [y, mo] = m.split("-");
      return { bulan: `${BULAN[Number(mo) - 1]} ${y.slice(2)}`, rata: Math.round(v.total / v.n) };
    });
  }, [entries]);

  if (loading) {
    return (
      <div className="grid place-items-center py-32 text-slate-500" data-testid="dashboard-loading">
        Memuat dashboard...
      </div>
    );
  }

  const kosong = !summary || summary.jumlah_data === 0;
  const naik = (summary?.perubahan ?? 0) >= 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Dashboard Grafik</h1>
          <p className="text-sm text-slate-500 mt-1">Tren posisi saldo harian dan ringkasan kinerja.</p>
        </div>
        <Link
          to="/entry"
          data-testid="dashboard-entry-cta"
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
        >
          <PenLine className="h-4 w-4" /> Entry Saldo Hari Ini
        </Link>
      </div>

      {kosong ? (
        <Card className="border-dashed" data-testid="dashboard-empty-state">
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <Wallet className="h-10 w-10 text-slate-300" />
            <p className="text-lg font-semibold text-slate-700">Belum ada data saldo</p>
            <p className="text-sm text-slate-500">
              Mulai dengan menginput posisi saldo pertama melalui menu Entry Saldo.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="dashboard-kpi-grid">
            <Card data-testid="kpi-saldo-terkini">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Saldo Terkini
                </CardTitle>
                <Wallet className="h-4 w-4 text-emerald-600" />
              </CardHeader>
              <CardContent>
                <p className="font-num text-xl sm:text-2xl font-bold text-slate-900">{fmtIDR(summary.saldo_terkini)}</p>
                <p className="text-xs text-slate-500 mt-1">per {fmtTanggal(summary.tanggal_terkini)}</p>
              </CardContent>
            </Card>

            <Card data-testid="kpi-perubahan">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Perubahan vs Sebelumnya
                </CardTitle>
                {naik ? (
                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-rose-600" />
                )}
              </CardHeader>
              <CardContent>
                <p className={`font-num text-xl sm:text-2xl font-bold ${naik ? "text-emerald-600" : "text-rose-600"}`}>
                  {summary.perubahan == null ? "-" : `${naik ? "+" : ""}${fmtIDR(summary.perubahan)}`}
                </p>
                <p className="text-xs text-slate-500 mt-1">{summary.jumlah_data} hari data tercatat</p>
              </CardContent>
            </Card>

            <Card data-testid="kpi-rata-rata">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Rata-rata Saldo
                </CardTitle>
                <Calculator className="h-4 w-4 text-slate-400" />
              </CardHeader>
              <CardContent>
                <p className="font-num text-xl sm:text-2xl font-bold text-slate-900">{fmtIDR(Math.round(summary.rata_rata))}</p>
                <p className="text-xs text-slate-500 mt-1">seluruh periode</p>
              </CardContent>
            </Card>

            <Card data-testid="kpi-tertinggi">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Saldo Tertinggi
                </CardTitle>
                <ArrowUpToLine className="h-4 w-4 text-amber-600" />
              </CardHeader>
              <CardContent>
                <p className="font-num text-xl sm:text-2xl font-bold text-slate-900">{fmtIDR(summary.tertinggi)}</p>
                <p className="text-xs text-slate-500 mt-1">terendah {fmtIDR(summary.terendah)}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
            <Card className="lg:col-span-3" data-testid="chart-line-saldo-trend">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Tren Saldo Harian</CardTitle>
              </CardHeader>
              <CardContent className="h-80">
                {ready && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={lineData} margin={{ top: 5, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#64748B" }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v) => (v >= 1e9 ? `${(v / 1e9).toFixed(1)} M` : v >= 1e6 ? `${(v / 1e6).toFixed(0)} jt` : v)}
                      width={56}
                    />
                    <Tooltip formatter={tooltipFmt} labelStyle={{ color: "#0F172A" }} />
                    <Line
                      type="monotone"
                      dataKey="saldo"
                      name="Total Saldo"
                      stroke="#059669"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "#059669" }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card data-testid="chart-bar-monthly-average">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Rata-rata per Bulan</CardTitle>
              </CardHeader>
              <CardContent className="h-80">
                {ready && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="bulan" tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} />
                    <YAxis
                      tick={{ fontSize: 10, fill: "#64748B" }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v) => (v >= 1e9 ? `${(v / 1e9).toFixed(1)}M` : v >= 1e6 ? `${(v / 1e6).toFixed(0)}jt` : v)}
                      width={44}
                    />
                    <Tooltip formatter={tooltipFmt} />
                    <Bar dataKey="rata" name="Rata-rata" fill="#0F172A" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
